import { useState } from "react";
import { createSlug } from "../../utils/slug";
import { $enum } from "ts-enum-util";
import { Editor } from "@tinymce/tinymce-react";
import {
  Box,
  SimpleGrid,
  GridItem,
  Input,
  FormLabel,
  Button,
  useToast,
  Flex,
  IconButton,
  Select,
} from "@chakra-ui/react";
import React from "react";
import PageLayout from "../../components/ui/PageLayout";
import { Link } from "react-router-dom";
import {
  CreateLessonMutationVariables,
  LanguageOptions,
  LessonQuery,
  SortOrder,
  UpdateLessonMutationVariables,
  useCategoriesQuery,
} from "../../generated/graphql";
import { FcList } from "react-icons/fc";
import ReactSelect from "react-select";

interface Props {
  handleCreateLesson?: (data: CreateLessonMutationVariables) => Promise<any>;
  handleUpdateLesson?: (data: UpdateLessonMutationVariables) => Promise<any>;
  id?: string;
  data?: LessonQuery["lesson"];
  actionType: "CREATE" | "UPDATE";
}

const Form: React.FC<Props> = ({
  handleCreateLesson,
  handleUpdateLesson,
  id,
  data,
  actionType,
}) => {
  const toast = useToast();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [headline, setHeadline] = useState("");
  const [categoryId, setCategoryId] = useState(data?.categoryId || "");
  const [isApplication, setIsApplication] = useState(data?.isApplication || false);

  const { data: categoriesData, loading: categoriesLoading } = useCategoriesQuery({
    variables: {
      orderBy: {
        order: SortOrder.asc,
      },
    },
    fetchPolicy: "network-only",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title && actionType === "CREATE") {
      return toast({
        status: "error",
        description: "Title is required",
        title: "Error",
        duration: 1000,
      });
    }

    try {
      if (actionType === "CREATE") {
        await handleCreateLesson!({
          data: {
            category: {
              connect: {
                id: categoryId,
              },
            },
            isApplication,
            descriptions: {
              create: [
                {
                  title,
                  description,
                  slug: createSlug(title),
                  headline,
                },
              ],
            },
          },
        });
      }
      if (actionType === "UPDATE") {
        await handleUpdateLesson!({
          where: {
            id: id,
          },
          data: {
            category: {
              connect: {
                id: categoryId,
              },
            },
            isApplication: {
              set: isApplication,
            },
          },
        });
      }
    } catch (error) {
      console.log("hata");
    }
  };

  return (
    <PageLayout>
      <Flex
        mb={6}
        justifyContent={"space-between"}
        alignItems="center"
        bgColor={"gray.100"}
        padding={2}
        rounded="md"
      >
        <Box fontSize={"18px"} fontWeight="bold">
          {actionType === "CREATE" ? "Create" : "Update"} Lesson
        </Box>
        <Flex experimental_spaceX={4}>
          <IconButton to={`/lesson`} as={Link} icon={<FcList />} aria-label="All Items" />
        </Flex>
      </Flex>
      <form onSubmit={handleSubmit}>
        <Box
          width={{
            base: "100%",
            md: "50%",
          }}
        >
          <SimpleGrid
            columns={2}
            gap={{
              base: 6,
              md: 6,
            }}
          >
            <GridItem colSpan={2}>
              <FormLabel>Category</FormLabel>
              <ReactSelect
                isLoading={categoriesLoading}
                isDisabled={categoriesLoading}
                isClearable
                {...(actionType === "UPDATE" &&
                  categoriesData && {
                    ...{
                      value: categoriesData.categories
                        .map((category) => ({
                          label: category.descriptions.find(
                            (d) => d.language === LanguageOptions.EN
                          )?.title,
                          value: category.id,
                        }))
                        .filter((c) => c.value === categoryId),
                    },
                  })}
                placeholder="Category"
                options={
                  categoriesData?.categories
                    ? categoriesData.categories.length > 0
                      ? categoriesData.categories.map((category) => {
                          return {
                            label: `${
                              category.descriptions.find(
                                (d) => d.language === LanguageOptions.TR
                              )?.title
                            }`,
                            value: category.id,
                          };
                        })
                      : []
                    : []
                }
                onChange={(e: any) => {
                  setCategoryId(e.value);
                }}
              />
            </GridItem>

            {actionType === "CREATE" && (
              <GridItem
                colSpan={{
                  base: 2,
                  md: 2,
                }}
              >
                <FormLabel>Title</FormLabel>

                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Title"
                />
              </GridItem>
            )}

            {actionType === "CREATE" && (
              <GridItem
                colSpan={{
                  base: 2,
                  md: 2,
                }}
              >
                <FormLabel>Headline</FormLabel>

                <Input
                  value={headline}
                  onChange={(e) => setHeadline(e.target.value)}
                  placeholder="Headline"
                />
              </GridItem>
            )}

            {actionType === "CREATE" && (
              <GridItem colSpan={2}>
                <FormLabel>Description</FormLabel>
                <Editor
                  apiKey="4vyocpjnhdhjtx3k08o1cv89jqhz5d6ae7910ubihafl7yes"
                  outputFormat="html"
                  onEditorChange={(e) => setDescription(e)}
                  init={{
                    skin: "snow",
                    icons: "thin",
                    placeholder: "Description about page",
                    height: 300,
                    menubar: false,
                    min_height: 200,
                    max_height: 500,

                    plugins: [
                      "advlist autolink lists link image charmap print preview anchor",
                      "searchreplace visualblocks code fullscreen",
                      "insertdatetime media table paste code wordcount",
                    ],
                    toolbar:
                      "undo redo | formatselect | " +
                      "bold italic backcolor forecolor | bullist numlist outdent indent | " +
                      "removeformat | code",
                  }}
                />
              </GridItem>
            )}

            <GridItem colSpan={1}>
              <Button
                width={"full"}
                colorScheme={!isApplication ? "green" : "gray"}
                onClick={() => setIsApplication(false)}
              >
                Course
              </Button>
            </GridItem>
            <GridItem colSpan={1}>
              <Button
                width="full"
                colorScheme={isApplication ? "green" : "gray"}
                onClick={() => setIsApplication(true)}
              >
                Application
              </Button>
            </GridItem>
          </SimpleGrid>
          <Button colorScheme={"green"} type="submit" mt={6}>
            Submit
          </Button>
        </Box>
      </form>
    </PageLayout>
  );
};

export default Form;
